import QueryLine from '@/components/query2/query-line';

export default class QueryCollectionValidator {
    /**
     * Initialize the validation builder for the provided items.
     * @param {QueryLine[]} items
     * @param {TimezoneHandler} timezone
     */
    constructor({items, timezone}) {
        this.timezone = timezone;
        this.items = items;
    }

    /**
     * Generate validators for the instance query items.
     * @return {{(*|Array|*[]|U[])}}
     */
    getValidators() {
        const mappedValidators = this.items
            .map(item => this.getTypeValidator(item));
        return {...mappedValidators};
    }

    /**
     * Get a function wrapping the definition provided into an object that
     * merges the secondary argument.
     * @param {{}} definition
     * @return {function(*=)}
     */
    static getBaseValidator(definition) {
        return (mergeTarget = {}) => ({...definition, ...mergeTarget});
    }

    /**
     * Build a validator for the query item.
     * @param {QueryLine} item
     * @return {{}}
     */
    getTypeValidator(item) {
        switch (item.type) {
            case QueryLine.Types.text:
                return QueryCollectionValidator.getTextValidator(item);
            case QueryLine.Types.numeric:
                return QueryCollectionValidator.getNumericValidator(item);
            case QueryLine.Types.datetime:
                return this.getDatetimeValidator(item);
            case QueryLine.Types.boolean:
            default:
                return {};
        }
    }

    /**
     * Build a validator for the text item.
     * @param {?[]} values
     * @return {{}}
     */
    static getTextValidator({values}) {
        return {
            safeCharacters() {
                // block special characters used for filtering as part of text values
                return (values || []).every(value => !/[,:;=!]/g.test(value));
            },
        };
    }

    /**
     * Build a validator for the numeric item. Takes into consideration the custom field
     * type `integer` versus a normal numeric.
     * @param {QueryLine} item
     * @return {{}}
     */
    static getNumericValidator(item) {
        const {operator, values} = item;
        const integerOnly = item.isCustomField
            && item.config.customFieldFormat === QueryLine.CustomFieldTypes.integer;
        const isNumeric = () => values
            .reduce((result, value) => result && !isNaN(value), true);
        const isInteger = () => values
            .reduce((result, value) => result && !isNaN(value) && /^\d+$/.test(value), true);
        const base = {
            [integerOnly ? 'integer' : 'numeric']: integerOnly ? isInteger : isNumeric,
        };
        const validator = QueryCollectionValidator.getBaseValidator(base);
        if (operator === QueryLine.Operators.isBetween) {
            const [min, max] = values;
            return validator({
                range() {
                    return min <= max;
                },
            });
        }
        return validator();
    }

    /**
     * Builds a datetime validator for the item. Only absolute dates require validation.
     * @param {string} operator
     * @param {string[]} values
     * @return {{dateRange(): *}|{}|boolean}
     */
    getDatetimeValidator({operator, values}) {
        if (operator === QueryLine.Operators.isAbsolute) {
            const [min, max] = values;
            if (min && max) {
                // only validate absolute ranges
                return {
                    dateRange: () => this.timezone.readUTC(min)
                        .valueOf() <= this.timezone.readUTC(max)
                        .valueOf(),
                };
            }
        }
        return {};
    }
}
